import ArchiveIcon from "@mui/icons-material/Inventory2Outlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import UnarchiveIcon from "@mui/icons-material/UnarchiveOutlined";
import { Button, Checkbox } from "@mui/joy";
import { Box, Paper } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Dispatch } from "@reduxjs/toolkit";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { useDispatch, useSelector } from "react-redux";
import {
  currencyFormatter,
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import { ProjectType_Entity } from "../../entities/projectType";
import {
  setFormOpen,
  setSelectedProjectTypeID,
  setShowArchivedRates,
  showArchivedRates,
} from "../../redux/filmsReducer";
import {
  useDeleteProjectTypeMutation,
  useUpdateProjectTypeMutation,
} from "../../redux/projectType/projectTypeEndpoints";
import { useProjectTypes } from "../../redux/projectType/projectTypeHooks";
import {
  getterDivider,
  setterDivider,
} from "../jobCategory/jobCategoryRates/dataGridColDef";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";

/**
 * ProjectType
 *
 * @returns {null} ProjectType
 */
export default function ProjectTypes() {
  const showArchived = useSelector(showArchivedRates);
  const { projectTypes: _projectTypes } = useProjectTypes();
  const projectTypes = _projectTypes?.filter(
    (pt) => showArchived || !pt.archived
  );
  const [deleteProjectType] = useDeleteProjectTypeMutation();
  const [updateProjectType] = useUpdateProjectTypeMutation();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const confirm = useConfirm();

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        maxHeight: "calc(100vh - 78px)",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        onRowClick={(e) => {
          dispatch(setSelectedProjectTypeID(e.row.id));
          dispatch(setFormOpen({ isOpen: true, formID: "projectType" }));
        }}
        apiRef={apiRef}
        rows={projectTypes ?? []}
        getRowClassName={(p) => (p.row.archived ? "archived" : "")}
        columns={projectTypeColumns(
          dispatch,
          confirm,
          deleteProjectType,
          updateProjectType
        )}
        showToolbar
        slots={{
          toolbar: ProjectTypeToolbar,
          noRowsOverlay: NoRowsOverlay,
        }}
      />
    </Paper>
  );
}

function ProjectTypeToolbar() {
  const dispatch = useDispatch();
  const showArchived = useSelector(showArchivedRates);

  return (
    <Toolbar>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 1,
          gap: 2,
          alignItems: "center",
          width: "100%",
        }}
      >
        <Button
          variant="soft"
          size="sm"
          onClick={() =>
            dispatch(setFormOpen({ formID: "projectType", isOpen: true }))
          }
        >
          + New Project Type
        </Button>
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
        <Checkbox
          size="sm"
          variant="outlined"
          label="Show Archived"
          onChange={(e) => dispatch(setShowArchivedRates(e.target.checked))}
          checked={showArchived}
        />
      </Box>
    </Toolbar>
  );
}

const projectTypeColumns = (
  dispatch: Dispatch,
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  deleteProjectType: (id: number) => void,
  updateProjectType: (args: {
    id: number;
    body: Partial<ProjectType_Entity>;
  }) => void
) =>
  injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      editable: false,
      type: "number",
      width: 40,
    },
    {
      field: "name",
      headerName: "Name",
      editable: true,
      type: "string",
      flex: 1,
    },
    {
      field: "flexPlan",
      headerName: "Flex Plan",
      editable: true,
      type: "boolean",
    },
    {
      field: "minimumCopyist",
      headerName: "Min Pay for Copyists",
      sortable: false,
      editable: true,
      width: 180,
      type: "number",
      valueGetter: getterDivider("minimumCopyist", 10000),
      valueSetter: setterDivider("minimumCopyist", 10000),
      valueFormatter: (valueOrParams: any) =>
        `${currencyFormatter.format(valueOrParams?.value ?? valueOrParams)}`,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (props: { id: any; row: ProjectType_Entity }) => [
        <GridActionsCellItem
          key="archive"
          showInMenu
          icon={props.row.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
          label={props.row.archived ? "Unarchive" : "Archive"}
          onClick={() => {
            if (props.row.archived) {
              updateProjectType({ id: props.id, body: { archived: false } });
              return;
            }
            confirm({
              description: `${props.row.name} will drop out of the rate grid and the project type picker. Projects already using it keep their rates, and "Show Archived" brings it back.`,
            })
              .then(() => {
                updateProjectType({ id: props.id, body: { archived: true } });
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
        <GridActionsCellItem
          key="delete"
          showInMenu
          icon={<DeleteIcon />}
          label={"Delete"}
          onClick={() => {
            confirm({
              description: `You are about to delete a projectType`,
            })
              .then(() => {
                deleteProjectType(props.id);
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
      ],
    },
  ]);
